"use client";

import { useState, useMemo, useEffect, useRef, useTransition } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { trackEvent } from "@/lib/analytics";
import {
  searchCandidates,
  type BrowseSearchFilters,
  type BrowseSearchResult,
  type BrowseCandidateResult,
  type MatchResultData,
  type PlacementDateRange,
  type AvailabilityWindowResult,
  type QualificationMatchMode,
} from "@/app/actions/browse";
import {
  ROLES_TREE,
  LOCATIONS_TREE,
  QUALIFICATIONS_TREE,
  EXPERIENCE_LEVELS,
  RIGHT_TO_WORK_REGIONS,
  NATIONALITIES,
  getAllLeafIdsFromTree,
  getAllLeafIds,
  getNodeCheckState,
  findLabelById,
  type TreeNode,
  buildFilteredRolesTree,
  getLocationsForSeasonRegion,
  getLocationsForSeasonGroup,
  ROLE_CATEGORIES,
  getInformalNationality,
  getRegionForLocation,
} from "@/lib/taxonomy";
import { CascadingTree } from "@/app/components/CascadingTree";
import {
  LanguageFilterPicker,
  type LanguageFilter,
} from "@/app/components/LanguageSelector";
import { createClient } from "@/lib/supabase";
import { sendFirstMessage } from "@/app/actions/messaging";
import { EmployerContactRequirements, useEmployerContactRequirements } from "@/app/_components/marketplace/EmployerContactRequirements";
import { toggleShortlist } from "@/app/actions/shortlist";
import {
  getAvailableSeasons,
  type SeasonPreset,
} from "@/lib/seasons";
import type { UserRole } from "@/lib/auth";
import { getLinkedProfileCounts } from "@/app/actions/profile-links";
import { getVouchCounts } from "@/app/actions/vouches";

// ═══ TYPES ═══

// Use server action types, with local aliases for readability
type AvailabilityWindow = AvailabilityWindowResult;
type BrowseCandidate = BrowseCandidateResult;
type MatchResult = MatchResultData;

interface EmployerInfo {
  id: string;
  company_name: string;
  contact_name: string;
  hiring_for: string[];
  daily_message_limit: number;
}

interface ConversationEntry {
  id: string;
  status: string;
  windowId: string | null;
}

// ═══ CONSTANTS ═══

const AVATAR_GRADIENTS = [
  "from-navy to-navy-mid",
  "from-terracotta to-gold",
  "from-sage to-[#5A7A5F]",
  "from-[#6A5ACD] to-[#8B78DB]",
  "from-[#2E86AB] to-[#45A0C4]",
  "from-navy to-sage",
];


// ═══ HELPERS ═══

function getInitials(first: string, last: string) {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function getDaysSinceActive(dateStr: string | null): number | null {
  if (!dateStr) return null;
  return Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / (24 * 60 * 60 * 1000)
  );
}

function getActivityIndicator(dateStr: string | null): { text: string; dotClass: string | null } {
  const days = getDaysSinceActive(dateStr);
  if (days === null) return { text: "New", dotClass: null };
  if (days < 1) return { text: "Active today", dotClass: "bg-success" };
  if (days === 1) return { text: "Active 1d ago", dotClass: "bg-[#F59E0B]" };
  if (days < 7) return { text: `Active ${days}d ago`, dotClass: "bg-[#F59E0B]" };
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return { text: `Active ${weeks}w ago`, dotClass: "bg-text-muted" };
  const months = Math.floor(days / 30);
  return { text: `Active ${months}mo ago`, dotClass: "bg-text-muted" };
}

function isInactive(dateStr: string | null): boolean {
  const days = getDaysSinceActive(dateStr);
  return days === null || days > 60;
}

function getSmartLocationLabels(selectedIds: string[]): string[] {
  if (!selectedIds || selectedIds.length === 0) return [];
  const selected = new Set(selectedIds);
  const labels: string[] = [];

  function walk(nodes: TreeNode[]) {
    for (const node of nodes) {
      const leaves = getAllLeafIds(node);
      if (leaves.length > 0 && leaves.every((id) => selected.has(id))) {
        labels.push(node.label);
      } else if (node.children && node.children.length > 0) {
        walk(node.children);
      }
    }
  }

  walk(LOCATIONS_TREE);
  return labels;
}

function getAge(dob: string): number | null {
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

// ═══ MATCHING HELPERS (for card rendering) ═══

/** Count confirmed placements for a window */
function getPlacementCount(
  windowId: string,
  placementMap: Record<string, PlacementDateRange[]>
): number {
  return (placementMap[windowId] ?? []).length;
}

// ═══ MESSAGE MODAL ═══

function MessageModal({
  candidate,
  matchingWindow,
  employer,
  onClose,
  onSent,
}: {
  candidate: BrowseCandidate;
  matchingWindow: AvailabilityWindow;
  employer: EmployerInfo;
  onClose: () => void;
  onSent: (conversationId: string) => void;
}) {
  const firstRole = employer.hiring_for?.[0]
    ? findLabelById(ROLES_TREE, employer.hiring_for[0]) ?? "a seasonal role"
    : "a seasonal role";
  const template = `Hi ${candidate.first_name}, I'm ${employer.contact_name} from ${employer.company_name}. We're looking for ${/^[aeiou]/i.test(firstRole) ? "an" : "a"} ${firstRole} for the upcoming season. I saw your profile and `;

  const [message, setMessage] = useState(template);
  const contact = useEmployerContactRequirements(candidate.id);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus trap + Escape key
  useEffect(() => {
    function handleKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    // Focus first interactive element on mount
    const timer = setTimeout(() => {
      modalRef.current?.querySelector<HTMLElement>("button, textarea")?.focus();
    }, 50);
    return () => { document.removeEventListener("keydown", handleKeyDown); clearTimeout(timer); };
  }, [onClose]);

  const charCount = message.length;
  const MAX_CHARS = 1000;

  const windowRoles = matchingWindow.positions
    .map((id) => findLabelById(ROLES_TREE, id))
    .filter(Boolean)
    .slice(0, 3);
  const windowLocations = getSmartLocationLabels(matchingWindow.preferred_locations).slice(0, 2);

  async function handleSend() {
    if (sending || !message.trim() || charCount > MAX_CHARS) return;
    if (contact.error) { setError(contact.error); return; }
    setSending(true);
    setError(null);

    const result = await sendFirstMessage(
      candidate.id,
      message,
      matchingWindow.id,
      contact.listingId || undefined
    );

    if (result.error) {
      if (result.conversationId) {
        onSent(result.conversationId);
        return;
      }
      setError(result.error);
      setSending(false);
      return;
    }

    trackEvent("message_sent", { initiated_by: "employer" });
    setSent(true);
    if (result.conversationId) {
      setTimeout(() => onSent(result.conversationId!), 2000);
    } else {
      setTimeout(onClose, 2500);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center backdrop-blur-[4px] p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label={`Message ${candidate.first_name}`} className="bg-white rounded-[20px] w-full max-w-[560px] max-h-[80vh] overflow-y-auto shadow-[0_24px_80px_rgba(0,0,0,0.2)] animate-fade-in">
        <div className="flex justify-between items-center px-5 sm:px-8 pt-7 pb-5 border-b border-border-light">
          <h3 className="font-serif text-[22px]">Send a Message</h3>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-[10px] bg-warm-bg border-none text-lg cursor-pointer flex items-center justify-center transition-all hover:bg-border"
          >
            &times;
          </button>
        </div>
        <div className="px-5 sm:px-8 py-7">
          {sent ? (
            <div className="text-center py-6">
              <div className="text-5xl mb-3">{"\u2705"}</div>
              <h4 className="font-serif text-lg mb-1.5">Message Sent!</h4>
              <p className="text-[13px] text-text-muted">
                {candidate.first_name} will receive your message and can reply
                through PeakWave. You&apos;ll get an email when they respond.
              </p>
            </div>
          ) : (
            <>
              <div className="flex gap-4 items-center mb-4 pb-4 border-b border-border-light">
                <div className="w-14 h-14 rounded-md bg-gradient-to-br from-navy to-navy-mid flex items-center justify-center font-serif text-xl text-white font-semibold shrink-0">
                  {getInitials(candidate.first_name, candidate.last_name)}
                </div>
                <div>
                  <div className="text-base font-semibold">
                    {candidate.first_name} {candidate.last_name}
                  </div>
                  <div className="text-xs text-text-muted">
                    Message will be sent through PeakWave
                  </div>
                </div>
              </div>

              {/* Window context */}
              <EmployerContactRequirements contact={contact} />
              <div className="bg-warm-bg rounded-[10px] px-4 py-3 mb-5 text-[12px] text-text-muted">
                <span className="font-semibold text-charcoal">Reaching out about:</span>{" "}
                {matchingWindow.season_label}
                {windowRoles.length > 0 && ` \u00B7 ${windowRoles.join(", ")}`}
                {windowLocations.length > 0 && ` \u00B7 ${windowLocations.join(", ")}`}
              </div>

              {error && (
                <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-3 mb-4 text-sm font-medium">
                  {error}
                </div>
              )}
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hi, I'm looking for a chalet host for our property this winter and your profile caught my eye. Would you be interested in discussing the role?"
                className="w-full min-h-[140px] px-4 py-3.5 border-[1.5px] border-border rounded-[12px] text-sm font-sans resize-y outline-none bg-warm-bg transition-colors focus:border-gold focus:bg-white"
              />
              <div className="flex justify-between items-center mt-2 mb-6">
                <p className="text-xs text-text-muted">
                  {"\uD83D\uDCA1"} Mention the specific role, location and dates
                </p>
                <span
                  className={`text-xs font-medium ${
                    charCount > MAX_CHARS ? "text-error" : "text-text-muted"
                  }`}
                >
                  {charCount}/{MAX_CHARS}
                </span>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-3 rounded-sm text-[13px] font-medium bg-transparent text-text-muted border-[1.5px] border-border cursor-pointer font-sans transition-all hover:border-charcoal hover:text-charcoal"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={!message.trim() || sending || charCount > MAX_CHARS}
                  className="px-7 py-3 rounded-sm text-[13px] font-semibold bg-navy text-white border-none cursor-pointer font-sans transition-all hover:bg-gold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending..." : "Send Message \u2192"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══ VIDEO MODAL ═══

function getYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match?.[1] ?? null;
}

function getVimeoId(url: string): string | null {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match?.[1] ?? null;
}

function VideoModal({
  candidate,
  onClose,
}: {
  candidate: BrowseCandidate;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const url = candidate.video_url!;
  const youtubeId = getYouTubeId(url);
  const vimeoId = getVimeoId(url);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-[6px]" />
      <div className="relative z-10 w-full max-w-[800px] animate-[scaleIn_0.25s_ease-out]">
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-20 w-10 h-10 md:w-10 md:h-10 rounded-full bg-black/50 text-white text-lg flex items-center justify-center cursor-pointer border-none transition-colors hover:bg-black/70 backdrop-blur-sm"
          style={{ minWidth: 48, minHeight: 48 }}
        >
          &times;
        </button>
        <div className="aspect-video rounded-[16px] overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.4)] bg-black">
          {youtubeId ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&mute=1&rel=0`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-none"
            />
          ) : vimeoId ? (
            <iframe
              src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1`}
              allow="autoplay; fullscreen"
              allowFullScreen
              className="w-full h-full border-none"
            />
          ) : (
            <video
              ref={videoRef}
              src={url}
              autoPlay
              muted
              controls
              playsInline
              className="w-full h-full object-contain"
            />
          )}
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-white font-semibold text-sm">
            {candidate.first_name} {candidate.last_name}
          </span>
        </div>
      </div>
    </div>
  );
}

// ═══ CANDIDATE CARD ═══

function CandidateCard({
  candidate,
  matchResult,
  index,
  onMessage,
  conversationInfo,
  isShortlisted,
  onToggleShortlist,
  onPlayVideo,
  isCheckingConversation,
  shortlistError,
  placementMap = {},
  userRole,
  linkedCount = 0,
  vouchCount = 0,
}: {
  candidate: BrowseCandidate;
  matchResult: MatchResult;
  index: number;
  onMessage: () => void;
  conversationInfo?: ConversationEntry;
  isShortlisted: boolean;
  onToggleShortlist: () => void;
  onPlayVideo: (candidate: BrowseCandidate) => void;
  isCheckingConversation?: boolean;
  shortlistError?: boolean;
  placementMap?: Record<string, PlacementDateRange[]>;
  userRole?: UserRole;
  linkedCount?: number;
  vouchCount?: number;
}) {
  const router = useRouter();
  const [showAllRoles, setShowAllRoles] = useState(false);
  const inactive = isInactive(candidate.last_active);
  const gradient = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];
  const allWindows = [matchResult.primaryWindow, ...matchResult.otherWindows];

  // Unique roles across all windows, sorted: specialist roles first
  const allPositionIds = [...new Set(allWindows.flatMap((w) => w.positions))];
  const positionLabels = allPositionIds
    .map((id) => findLabelById(ROLES_TREE, id))
    .filter(Boolean) as string[];
  const extraRoles = Math.max(0, positionLabels.length - 3);

  // Locations from all windows — show region names if multi-region
  const allLocationIds = [...new Set(allWindows.flatMap((w) => w.preferred_locations))];
  const distinctRegions = [...new Set(
    allLocationIds.map((id) => getRegionForLocation(id)).filter(Boolean)
  )] as string[];
  const locationLabels = distinctRegions.length > 1
    ? distinctRegions
    : getSmartLocationLabels(allLocationIds);
  const age = candidate.birth_date ? getAge(candidate.birth_date) : null;
  const activity = getActivityIndicator(candidate.last_active);

  // Count total placements across all visible windows
  const totalPlacements = allWindows.reduce(
    (sum, w) => sum + getPlacementCount(w.id, placementMap), 0
  );

  function handleMessageClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (conversationInfo) {
      router.push(`/employer/messages/${conversationInfo.id}`);
    } else {
      onMessage();
    }
  }

  return (
    <div
      onClick={() =>
        router.push(
          // Logged-out visitors are sent to employer sign-up; full profiles
          // are only for signed-in employers. Sign-up has no redirect
          // parameter, so the profile path is not passed along.
          userRole ? `/candidates/${candidate.id}` : "/employer-signup",
        )
      }
      className={`group relative bg-white rounded-[12px] border border-border-light transition-all cursor-pointer hover:border-[rgba(0,0,0,0.12)] hover:shadow-[0_2px_12px_rgba(0,0,0,0.05)] ${
        inactive ? "opacity-40 grayscale" : ""
      }`}
      title={inactive ? "Inactive for 60+ days" : undefined}
    >
      <div className="px-4 py-3.5 md:flex md:items-center md:gap-4">
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Avatar + name + meta */}
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <div
                className={`w-11 h-11 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center font-serif text-sm text-white font-semibold overflow-hidden ${candidate.featured ? "ring-2 ring-gold ring-offset-1" : ""}`}
              >
                {candidate.photo_url ? (
                  <Image
                    src={candidate.photo_url}
                    alt={`${candidate.first_name} ${candidate.last_name}`}
                    width={44}
                    height={44}
                    sizes="44px"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  getInitials(candidate.first_name, candidate.last_name)
                )}
              </div>
              {candidate.featured && (
                candidate.boost_source === 'season_edit' ? (
                  <span
                    className="absolute -bottom-0.5 -right-0.5 px-1 h-[16px] bg-gold rounded-full flex items-center justify-center border-[1.5px] border-white text-white text-[7px] font-semibold tracking-[0.3px] whitespace-nowrap"
                    title="Featured on the Season Edit"
                  >
                    EDIT
                  </span>
                ) : (
                  <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-gold rounded-full flex items-center justify-center border-[1.5px] border-white text-white text-[8px]" title="Featured candidate">&#9733;</span>
                )
              )}
              {candidate.video_url && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlayVideo(candidate);
                  }}
                  className="absolute -top-0.5 -left-0.5 w-5 h-5 rounded-full bg-navy text-white flex items-center justify-center cursor-pointer border-[1.5px] border-white transition-transform hover:scale-[1.12] p-0"
                  aria-label="Play video intro"
                >
                  <svg width="8" height="8" viewBox="0 0 10 10" fill="white">
                    <polygon points="3,1.5 8,5 3,8.5" />
                  </svg>
                </button>
              )}
            </div>
            <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-[14px] text-charcoal group-hover:underline decoration-charcoal/30 underline-offset-2">
                {candidate.first_name} {candidate.last_name}
              </span>
              {age != null && (
                <span className="text-[12px] text-text-muted">{age}</span>
              )}
              {(candidate.current_location || candidate.nationality) && (
                <>
                  <span className="text-text-muted text-[9px]">&middot;</span>
                  <span className="text-[11px] text-text-muted">
                    {candidate.current_location && `Based in ${candidate.current_location}`}
                    {candidate.current_location && candidate.nationality && " · "}
                    {candidate.nationality && getInformalNationality(candidate.nationality)}
                  </span>
                </>
              )}
              <span className="text-text-muted text-[9px]">&middot;</span>
              <span className="text-[11px] text-text-muted inline-flex items-center gap-1">
                {activity.dotClass && <span className={`w-1.5 h-1.5 rounded-full ${activity.dotClass} shrink-0`} />}
                {inactive ? "Inactive 60+ days" : activity.text}
              </span>
              {linkedCount > 0 && (
                <>
                  <span className="text-text-muted text-[9px]">&middot;</span>
                  <span className="text-[11px] text-gold inline-flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    Travelling with {linkedCount}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Row 2: Availability windows (max 3, matching seasons first) */}
          <div className="mt-2 space-y-0.5">
            {allWindows.slice(0, 3).map((w) => {
              const from = new Date(w.available_from).toLocaleDateString("en-GB", { month: "short" });
              const to = new Date(w.available_to).toLocaleDateString("en-GB", { month: "short" });
              return (
                <div key={w.id} className="flex items-center gap-1 text-[12.5px]">
                  <span className="text-navy font-medium">{w.season_label}</span>
                  <span className="text-text-muted">&middot;</span>
                  <span className="text-text-muted">{from} &ndash; {to}</span>
                </div>
              );
            })}
            {allWindows.length > 3 && (
              <span className="text-[11px] text-text-muted">
                +{allWindows.length - 3} more season{allWindows.length - 3 !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Row 3: Location (single line, truncated) */}
          {locationLabels.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1 min-w-0">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0 text-text-muted"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span
                className="text-[12px] text-text-muted overflow-hidden text-ellipsis whitespace-nowrap"
                title={locationLabels.join(" \u00B7 ")}
              >
                {locationLabels.join(" \u00B7 ")}
              </span>
            </div>
          )}

          {/* Row 4: Role pills (max 3 + overflow) + placement chip */}
          {(positionLabels.length > 0 || totalPlacements > 0 || vouchCount > 0) && (
            <div className="flex flex-wrap gap-1 mt-2">
              {(showAllRoles ? positionLabels : positionLabels.slice(0, 3)).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 rounded-[4px] text-[11px] font-medium bg-[#EEF2FF] text-navy"
                >
                  {tag}
                </span>
              ))}
              {extraRoles > 0 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowAllRoles(!showAllRoles); }}
                  className="px-2 py-0.5 rounded-[4px] text-[11px] font-medium bg-warm-bg text-text-muted hover:text-text-body transition-colors cursor-pointer border-none"
                >
                  {showAllRoles ? "Show less" : `+${extraRoles} more`}
                </button>
              )}
              {totalPlacements > 0 && (
                <span className="px-2 py-0.5 rounded-[4px] text-[11px] font-medium bg-[#E8EDF4] text-[#5A6B82]">
                  {totalPlacements} placement{totalPlacements !== 1 ? "s" : ""}
                </span>
              )}
              {vouchCount > 0 && (
                <span className="px-2 py-0.5 rounded-[4px] text-[11px] font-medium bg-[#E8F5E9] text-[#2E7D32] inline-flex items-center gap-1">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  {vouchCount} vouch{vouchCount !== 1 ? "es" : ""}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions: bottom on mobile, right side on desktop */}
        <div className="flex gap-2 mt-2.5 pt-2 border-t border-border-light md:mt-0 md:pt-0 md:border-t-0 md:flex-col md:shrink-0">
          <div className="flex-1 md:flex-initial">
            {candidate.is_sample ? (
              <span
                className="block w-full px-3 py-1.5 rounded-sm text-[12px] font-semibold bg-border/50 text-text-muted border-none whitespace-nowrap cursor-default text-center min-h-[36px] md:min-w-[120px] leading-[21px]"
                title="This is a sample profile. Messaging is disabled"
              >
                Message
              </span>
            ) : userRole === "candidate" ? (
              <span
                className="block w-full px-3 py-1.5 rounded-sm text-[12px] font-semibold bg-border/50 text-text-muted border-none whitespace-nowrap cursor-not-allowed text-center min-h-[36px] md:min-w-[120px] leading-[21px]"
                title="Sign up as an employer to message candidates"
                onClick={(e) => e.stopPropagation()}
              >
                Message
              </span>
            ) : !userRole ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  router.push("/employer-signup?redirect=/browse");
                }}
                className="w-full px-3 py-1.5 rounded-sm text-[12px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light min-h-[36px] md:min-w-[120px] md:whitespace-nowrap"
              >
                Sign up to message
              </button>
            ) : (
              <button
                onClick={handleMessageClick}
                disabled={isCheckingConversation}
                className="w-full px-3 py-1.5 rounded-sm text-[12px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light whitespace-nowrap disabled:opacity-60 disabled:cursor-wait min-h-[36px] md:min-w-[120px]"
              >
                {isCheckingConversation
                  ? "..."
                  : conversationInfo
                    ? "View Conversation"
                    : "Message"}
              </button>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleShortlist();
            }}
            aria-label={isShortlisted ? "Remove from shortlist" : "Save to shortlist"}
            className={`flex-1 md:flex-initial px-3 py-1.5 rounded-sm text-[12px] font-semibold border cursor-pointer font-sans transition-all whitespace-nowrap inline-flex items-center justify-center gap-1 min-h-[36px] md:min-w-[120px] ${
              isShortlisted
                ? "bg-transparent text-gold border-gold"
                : "bg-transparent text-text-muted border-[rgba(0,0,0,0.10)] hover:border-gold hover:text-gold"
            }`}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill={isShortlisted ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
            {isShortlisted ? "Saved" : "Save"}
          </button>
        </div>
        {shortlistError && (
          <p className="text-[11px] text-red-500 text-right mt-1 animate-fade-in">
            Could not update shortlist. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}



// ═══ SEASON FILTER ═══

function SeasonFilter({
  selectedSeason,
  onSelectSeason,
  employerDatesActive,
  employerFrom,
  employerTo,
  onToggleDates,
  onSetFrom,
  onSetTo,
  onClearDates,
}: {
  selectedSeason: SeasonPreset | null;
  onSelectSeason: (preset: SeasonPreset | null) => void;
  employerDatesActive: boolean;
  employerFrom: string;
  employerTo: string;
  onToggleDates: () => void;
  onSetFrom: (v: string) => void;
  onSetTo: (v: string) => void;
  onClearDates: () => void;
}) {
  const { snowPresets, sunPresets, festivalPresets } = useMemo(() => {
    const groups = getAvailableSeasons();
    const all = groups.flatMap((g) => g.presets);
    return {
      snowPresets: all.filter((p) => p.displayGroup === "snow"),
      sunPresets: all.filter((p) => p.displayGroup === "sun"),
      festivalPresets: all.filter((p) => p.displayGroup === "festival"),
    };
  }, []);

  const allPresets = useMemo(
    () => [...snowPresets, ...sunPresets, ...festivalPresets],
    [snowPresets, sunPresets, festivalPresets]
  );

  function handleSeasonChange(label: string) {
    if (!label) {
      onSelectSeason(null);
      return;
    }
    const preset = allPresets.find((p) => p.label === label);
    if (preset) onSelectSeason(preset);
  }

  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-2 text-text-muted">
        Season
      </label>
      <select
        value={selectedSeason?.label ?? ""}
        onChange={(e) => handleSeasonChange(e.target.value)}
        className="w-full px-3 py-2.5 border-[1.5px] border-border rounded-sm text-[13px] font-sans bg-warm-bg outline-none focus:border-gold"
      >
        <option value="">All Seasons</option>
        <optgroup label="Snow Seasons">
          {snowPresets.map((p) => (
            <option key={p.label} value={p.label}>
              {p.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Sun Seasons">
          {sunPresets.map((p) => (
            <option key={p.label} value={p.label}>
              {p.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Festival Seasons">
          {festivalPresets.map((p) => (
            <option key={p.label} value={p.label}>
              {p.label}
            </option>
          ))}
        </optgroup>
      </select>

      {/* Progressive date filter */}
      {selectedSeason && (
        <div className="mt-3 pt-3 border-t border-border-light">
          {!employerDatesActive ? (
            <button
              onClick={onToggleDates}
              className="text-[12px] text-text-muted hover:text-gold transition-colors bg-transparent border-none cursor-pointer font-sans"
            >
              Filter by specific dates ▾
            </button>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.8px] text-text-muted">
                  Your Dates
                </span>
                <button
                  onClick={onClearDates}
                  className="text-[11px] text-text-muted hover:text-gold transition-colors bg-transparent border-none cursor-pointer font-sans"
                >
                  Clear dates
                </button>
              </div>
              <div className="flex flex-col gap-1.5">
                <input
                  type="date"
                  value={employerFrom}
                  onChange={(e) => onSetFrom(e.target.value)}
                  className="w-full px-3 py-2.5 border-[1.5px] border-border rounded-sm text-[13px] font-sans bg-warm-bg outline-none focus:border-gold"
                />
                <span className="text-[11px] text-text-muted text-center">to</span>
                <input
                  type="date"
                  value={employerTo}
                  onChange={(e) => onSetTo(e.target.value)}
                  className="w-full px-3 py-2.5 border-[1.5px] border-border rounded-sm text-[13px] font-sans bg-warm-bg outline-none focus:border-gold"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══ FILTER SIDEBAR ═══

interface Filters {
  season: SeasonPreset | null;
  seasonDisplayGroup: string; // "snow" | "sun" | "festival" | ""
  employerDatesActive: boolean;
  employerFrom: string;
  employerTo: string;
  roleCategory: string;
  roles: Set<string>;
  locations: Set<string>;
  qualifications: Set<string>;
  qualificationMatchMode: QualificationMatchMode;
  languages: LanguageFilter[];
  experience: string;
  rightToWork: string;
  rightToWorkMode: string;
  ageRanges: Set<string>;
  gender: string;
  nationality: string;
  hasVideo: boolean;
}

const ROLE_CATEGORY_LABELS: Record<string, string> = {
  ski: "Ski & Winter",
  yacht: "Yacht",
  watersports: "Watersports",
  festivals: "Festivals",
  hospitality: "Hospitality",
  agriculture: "Agriculture",
};

const DISPLAY_GROUP_LABELS: Record<string, string> = {
  snow: "Winter",
  sun: "Summer",
  festival: "Festivals",
};

const INITIAL_FILTERS: Filters = {
  season: null,
  seasonDisplayGroup: "",
  employerDatesActive: false,
  employerFrom: "",
  employerTo: "",
  roleCategory: "",
  roles: new Set<string>(),
  locations: new Set<string>(),
  qualifications: new Set<string>(),
  qualificationMatchMode: "any",
  languages: [],
  experience: "",
  rightToWork: "",
  rightToWorkMode: "all",
  ageRanges: new Set<string>(),
  gender: "",
  nationality: "",
  hasVideo: false,
};

function FilterSidebar({
  filters,
  setFilters,
  filteredCount,
  nameSearch,
  onNameSearchChange,
  onClose,
  showEmployerOnlyFilters,
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  filteredCount: number;
  nameSearch: string;
  onNameSearchChange: (value: string) => void;
  onClose?: () => void;
  // Age, gender, nationality and video filters are for signed-in employers
  // only; the server ignores them for everyone else (browse-privacy.ts).
  showEmployerOnlyFilters: boolean;
}) {
  function clearAll() {
    setFilters(INITIAL_FILTERS);
    onNameSearchChange("");
  }

  const hasFilters =
    nameSearch !== "" ||
    filters.season !== null ||
    filters.seasonDisplayGroup !== "" ||
    filters.roleCategory !== "" ||
    filters.roles.size > 0 ||
    filters.locations.size > 0 ||
    filters.qualifications.size > 0 ||
    filters.languages.length > 0 ||
    filters.experience !== "" ||
    filters.ageRanges.size > 0 ||
    filters.gender !== "" ||
    filters.rightToWork !== "" ||
    filters.nationality !== "" ||
    filters.hasVideo;

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-start mb-1">
        <h3 className="font-serif text-xl">Find Candidates</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden w-8 h-8 rounded-sm bg-warm-bg border-none text-lg cursor-pointer flex items-center justify-center"
          >
            &times;
          </button>
        )}
      </div>
      <p className="text-xs text-text-muted mb-5">
        {hasFilters
          ? `${filteredCount} candidate${filteredCount !== 1 ? "s" : ""} matching your search`
          : `${filteredCount} candidate${filteredCount !== 1 ? "s" : ""}`}
      </p>

      {hasFilters && (
        <button
          onClick={clearAll}
          className="w-full text-[11px] text-text-muted hover:text-gold transition-colors bg-transparent border-none cursor-pointer font-sans mb-4 text-right"
        >
          Clear all filters
        </button>
      )}

      <div className="flex-1 overflow-y-auto space-y-5 pb-4">
        {/* Name Search */}
        <div className="relative">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={nameSearch}
            onChange={(e) => onNameSearchChange(e.target.value)}
            placeholder="Search by name..."
            className="w-full pl-9 pr-3 py-2.5 border border-border rounded-lg text-sm font-sans text-charcoal bg-warm-bg outline-none transition-colors focus:border-gold focus:bg-white placeholder:text-text-muted"
          />
        </div>

        {/* Season Filter */}
        <SeasonFilter
          selectedSeason={filters.season}
          onSelectSeason={(preset) =>
            setFilters((f) => {
              // Compute new allowed sets so we can prune stale selections
              const newRolesTree = preset
                ? (buildFilteredRolesTree(preset.region) ?? ROLES_TREE)
                : ROLES_TREE;
              const newLocsTree = preset
                ? (getLocationsForSeasonRegion(preset.region) ?? LOCATIONS_TREE)
                : LOCATIONS_TREE;
              const allowedRoles = new Set(getAllLeafIdsFromTree(newRolesTree));
              const allowedLocs = new Set(getAllLeafIdsFromTree(newLocsTree));

              // Smart default for right-to-work based on season region
              const SEASON_RTW: Record<string, string> = {
                "European Alps": "EU / Schengen",
                "Scandinavia": "EU / Schengen",
                "Mediterranean": "EU / Schengen",
                "European Festivals": "EU / Schengen",
                "North America": "USA",
                "North American Festivals": "USA",
                "UK Festivals": "UK",
                "Australia & Pacific": "Australia",
                "Australasia Festivals": "Australia",
                "Southern Hemisphere": "New Zealand",
              };
              const defaultRtw = preset ? (SEASON_RTW[preset.region] ?? "") : "";

              return {
                ...f,
                season: preset,
                seasonDisplayGroup: "",
                employerDatesActive: false,
                employerFrom: preset?.from ?? "",
                employerTo: preset?.to ?? "",
                roles: new Set([...f.roles].filter((id) => allowedRoles.has(id))),
                locations: new Set([...f.locations].filter((id) => allowedLocs.has(id))),
                rightToWork: defaultRtw,
              };
            })
          }
          employerDatesActive={filters.employerDatesActive}
          employerFrom={filters.employerFrom}
          employerTo={filters.employerTo}
          onToggleDates={() =>
            setFilters((f) => ({
              ...f,
              employerDatesActive: true,
              employerFrom: f.season?.from ?? f.employerFrom,
              employerTo: f.season?.to ?? f.employerTo,
            }))
          }
          onSetFrom={(v) => setFilters((f) => ({ ...f, employerFrom: v }))}
          onSetTo={(v) => setFilters((f) => ({ ...f, employerTo: v }))}
          onClearDates={() =>
            setFilters((f) => ({
              ...f,
              employerDatesActive: false,
              employerFrom: f.season?.from ?? "",
              employerTo: f.season?.to ?? "",
            }))
          }
        />

        {/* Roles */}
        <CascadingTree
          tree={filters.season
            ? (buildFilteredRolesTree(filters.season.region) ?? ROLES_TREE)
            : ROLES_TREE}
          selected={filters.roleCategory ? new Set(ROLE_CATEGORIES[filters.roleCategory]) : filters.roles}
          onChange={(roles) => setFilters((f) => ({ ...f, roles, roleCategory: "" }))}
          label="Roles"
          maxHeight="240px"
          searchable
        />

        {/* Locations */}
        <CascadingTree
          tree={filters.season
            ? (getLocationsForSeasonRegion(filters.season.region) ?? LOCATIONS_TREE)
            : filters.seasonDisplayGroup
              ? (getLocationsForSeasonGroup(filters.seasonDisplayGroup) ?? LOCATIONS_TREE)
              : LOCATIONS_TREE}
          selected={filters.locations}
          onChange={(locations) => setFilters((f) => ({ ...f, locations }))}
          label="Locations"
          maxHeight="240px"
          searchable
        />

        {/* Qualifications */}
        <CascadingTree
          tree={QUALIFICATIONS_TREE}
          selected={filters.qualifications}
          onChange={(qualifications) =>
            setFilters((f) => ({
              ...f,
              qualifications,
              qualificationMatchMode:
                qualifications.size > 0 ? f.qualificationMatchMode : "any",
            }))
          }
          label="Qualifications"
          maxHeight="200px"
        />
        {filters.qualifications.size > 1 && (
          <label className="flex min-h-11 items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.qualificationMatchMode === "all"}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  qualificationMatchMode: e.target.checked ? "all" : "any",
                }))
              }
              className="w-4.5 h-4.5 rounded accent-gold cursor-pointer"
            />
            <span className="text-[12px] text-text-muted">
              Require every selected qualification
            </span>
          </label>
        )}

        {/* Languages */}
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-2">
            Languages
          </label>
          <LanguageFilterPicker
            value={filters.languages}
            onChange={(languages) =>
              setFilters((f) => ({ ...f, languages }))
            }
          />
        </div>

        {/* Experience */}
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-2">
            Experience Level
          </label>
          <select
            value={filters.experience}
            onChange={(e) =>
              setFilters((f) => ({ ...f, experience: e.target.value }))
            }
            className="w-full px-3 py-2.5 border-[1.5px] border-border rounded-sm text-[13px] font-sans bg-warm-bg outline-none focus:border-gold"
          >
            <option value="">Any Experience</option>
            {EXPERIENCE_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </div>

        {showEmployerOnlyFilters && (<>
        {/* Age Range */}
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-2">
            Age Range
          </label>
          <div className="flex flex-wrap gap-1.5">
            {[
              { value: "under-21", label: "Under 21" },
              { value: "21-25", label: "21-25" },
              { value: "26-30", label: "26-30" },
              { value: "30+", label: "30+" },
            ].map((opt) => {
              const selected = filters.ageRanges.has(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    setFilters((f) => {
                      const next = new Set(f.ageRanges);
                      if (next.has(opt.value)) next.delete(opt.value);
                      else next.add(opt.value);
                      return { ...f, ageRanges: next };
                    })
                  }
                  className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors cursor-pointer ${
                    selected
                      ? "bg-gold/10 text-gold border-gold/30"
                      : "bg-warm-bg text-text-muted border-border hover:border-gold/40 hover:text-text-body"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Gender */}
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-2">
            Gender
          </label>
          <select
            value={filters.gender}
            onChange={(e) =>
              setFilters((f) => ({ ...f, gender: e.target.value }))
            }
            className="w-full px-3 py-2.5 border-[1.5px] border-border rounded-sm text-[13px] font-sans bg-warm-bg outline-none focus:border-gold"
          >
            <option value="">Any Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>

        </>)}

        {/* Available to Work In */}
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-2">
            Available to Work In
          </label>
          <select
            value={filters.rightToWork}
            onChange={(e) =>
              setFilters((f) => ({ ...f, rightToWork: e.target.value, rightToWorkMode: "all" }))
            }
            className="w-full px-3 py-2.5 border-[1.5px] border-border rounded-sm text-[13px] font-sans bg-warm-bg outline-none focus:border-gold"
          >
            <option value="">Any Region</option>
            {RIGHT_TO_WORK_REGIONS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          {filters.rightToWork && (
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.rightToWorkMode === "yes_only"}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, rightToWorkMode: e.target.checked ? "yes_only" : "all" }))
                }
                className="w-4.5 h-4.5 rounded accent-gold cursor-pointer"
              />
              <span className="text-[12px] text-text-muted">Full right to work only (no sponsorship needed)</span>
            </label>
          )}
        </div>

        {showEmployerOnlyFilters && (<>
        {/* Nationality */}
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-2">
            Nationality
          </label>
          <select
            value={filters.nationality}
            onChange={(e) =>
              setFilters((f) => ({ ...f, nationality: e.target.value }))
            }
            className="w-full px-3 py-2.5 border-[1.5px] border-border rounded-sm text-[13px] font-sans bg-warm-bg outline-none focus:border-gold"
          >
            <option value="">Any Nationality</option>
            {NATIONALITIES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        {/* Has Video */}
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block mb-2">
            Has Video Intro
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.hasVideo}
              onChange={(e) =>
                setFilters((f) => ({ ...f, hasVideo: e.target.checked }))
              }
              className="w-4 h-4 rounded accent-gold cursor-pointer"
            />
            <span className="text-[13px]">Yes</span>
          </label>
        </div>
        </>)}
      </div>

      {/* Spacer so content doesn't hide behind sticky button */}
      {onClose && <div className="md:hidden h-16" />}
      {/* Mobile apply button — sticky at bottom */}
      {onClose && (
        <div className="md:hidden sticky bottom-0 left-0 right-0 bg-white pt-2 pb-3 -mx-5 px-5 border-t border-border-light">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gold text-white rounded-lg text-[14px] font-semibold border-none cursor-pointer font-sans transition-all hover:bg-gold-light shadow-[0_2px_8px_rgba(28, 138, 153,0.25)]"
          >
            Show {filteredCount} Candidate{filteredCount !== 1 ? "s" : ""}
          </button>
        </div>
      )}
    </div>
  );
}

// ═══ MAIN COMPONENT ═══

const PAGE_SIZE = 24;

function serializeFilters(filters: Filters): BrowseSearchFilters {
  return {
    seasonLabel: filters.season?.label ?? null,
    seasonDisplayGroup: filters.seasonDisplayGroup,
    employerDatesActive: filters.employerDatesActive,
    employerFrom: filters.employerFrom,
    employerTo: filters.employerTo,
    roleCategory: filters.roleCategory,
    roles: Array.from(filters.roles),
    locations: Array.from(filters.locations),
    qualifications: Array.from(filters.qualifications),
    qualificationMatchMode:
      filters.qualifications.size > 0 ? filters.qualificationMatchMode : "any",
    languages: filters.languages.map((l) => ({
      language: l.language,
      minProficiency: l.minProficiency,
    })),
    experience: filters.experience,
    rightToWork: filters.rightToWork,
    rightToWorkMode: filters.rightToWorkMode,
    ageRanges: Array.from(filters.ageRanges),
    gender: filters.gender,
    nationality: filters.nationality,
    hasVideo: filters.hasVideo,
  };
}

function deserializeFilters(sf: BrowseSearchFilters): Partial<Filters> {
  const partial: Partial<Filters> = {};
  if (sf.seasonDisplayGroup) partial.seasonDisplayGroup = sf.seasonDisplayGroup;
  if (sf.roleCategory) partial.roleCategory = sf.roleCategory;
  if (sf.roles.length > 0) partial.roles = new Set(sf.roles);
  if (sf.locations.length > 0) partial.locations = new Set(sf.locations);
  if (sf.qualifications.length > 0) {
    partial.qualifications = new Set(sf.qualifications);
    partial.qualificationMatchMode = sf.qualificationMatchMode ?? "any";
  }
  if (sf.seasonLabel) {
    const groups = getAvailableSeasons();
    const allPresets = groups.flatMap((g) => g.presets);
    const preset = allPresets.find((p) => p.label === sf.seasonLabel);
    if (preset) {
      partial.season = preset;
      partial.employerFrom = sf.employerFrom;
      partial.employerTo = sf.employerTo;
      partial.employerDatesActive = sf.employerDatesActive;
    }
  }
  return partial;
}

export function BrowseContent({
  initialResults,
  initialFilters,
  userRole,
  employer,
  conversationMap,
  shortlistedIds: initialShortlistedIds,
}: {
  initialResults: BrowseSearchResult;
  initialFilters: BrowseSearchFilters;
  userRole?: UserRole;
  employer?: EmployerInfo | null;
  conversationMap?: Record<string, ConversationEntry[]>;
  shortlistedIds?: string[];
}) {
  const router = useRouter();
  const supabase = createClient();
  const isEmployer = userRole === "employer";
  const [isPending, startTransition] = useTransition();
  const [filters, setFilters] = useState<Filters>(() => {
    // Reconstruct client Filters from server-provided initialFilters
    const base = { ...INITIAL_FILTERS };
    const deserialized = deserializeFilters(initialFilters);
    return { ...base, ...deserialized };
  });
  // Server-side pagination state
  const [loadedItems, setLoadedItems] = useState<{ candidate: BrowseCandidate; match: MatchResult }[]>(
    initialResults.items
  );
  const [placementMap, setPlacementMap] = useState<Record<string, PlacementDateRange[]>>(
    initialResults.placementMap
  );
  const [totalCount, setTotalCount] = useState(initialResults.total);
  const [hasMore, setHasMore] = useState(initialResults.hasMore);
  const [loadingMore, setLoadingMore] = useState(false);

  const [messageTarget, setMessageTarget] = useState<{
    candidate: BrowseCandidate;
    window: AvailabilityWindow;
  } | null>(null);
  const [videoCandidate, setVideoCandidate] = useState<BrowseCandidate | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [shortlistedSet, setShortlistedSet] = useState<Set<string>>(
    () => new Set(initialShortlistedIds ?? [])
  );
  const [showShortlistedOnly, setShowShortlistedOnly] = useState(false);
  const [nameSearch, setNameSearch] = useState("");
  const [debouncedNameSearch, setDebouncedNameSearch] = useState("");
  const [checkingConversationFor, setCheckingConversationFor] = useState<string | null>(null);
  const [shortlistError, setShortlistError] = useState<string | null>(null);
  const [linkedCounts, setLinkedCounts] = useState<Record<string, number>>({});
  const [vouchCounts, setVouchCounts] = useState<Record<string, number>>({});

  // Fetch linked profile counts and vouch counts for displayed candidates
  useEffect(() => {
    const userIds = loadedItems.map((item) => item.candidate.id);
    const newLinkedIds = userIds.filter((id) => !(id in linkedCounts));
    if (newLinkedIds.length > 0) {
      getLinkedProfileCounts(newLinkedIds).then(({ counts }) => {
        if (Object.keys(counts).length > 0) {
          setLinkedCounts((prev) => ({ ...prev, ...counts }));
        }
      });
    }
    const newVouchIds = userIds.filter((id) => !(id in vouchCounts));
    if (newVouchIds.length > 0) {
      getVouchCounts(newVouchIds).then((counts) => {
        if (Object.keys(counts).length > 0) {
          setVouchCounts((prev) => ({ ...prev, ...counts }));
        }
      });
    }
  }, [loadedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Welcome toast from URL param — one-time mount hydration from URL state
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("welcome") === "true") {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- mount-only URL param hydration; cascading render is intentional and once-only
      setShowWelcome(true);
      window.history.replaceState({}, "", "/browse");
      setTimeout(() => setShowWelcome(false), 5000);
    }
  }, []);

  // Prefetch adjacent season group results to warm the cache
  const prefetchedRef = useRef(false);
  useEffect(() => {
    if (prefetchedRef.current) return;
    prefetchedRef.current = true;
    const emptyFilters: BrowseSearchFilters = {
      seasonLabel: null, seasonDisplayGroup: "",
      employerDatesActive: false, employerFrom: "", employerTo: "",
      roleCategory: "", roles: [], locations: [], qualifications: [],
      qualificationMatchMode: "any",
      languages: [], experience: "", rightToWork: "", rightToWorkMode: "all",
      ageRanges: [], gender: "", nationality: "", hasVideo: false,
    };
    const prefetch = () => {
      for (const group of ["snow", "sun", "festival"]) {
        searchCandidates({ ...emptyFilters, seasonDisplayGroup: group }, 0, PAGE_SIZE);
      }
    };
    if (typeof requestIdleCallback !== "undefined") {
      requestIdleCallback(prefetch);
    } else {
      setTimeout(prefetch, 2000);
    }
  }, []);

  // Server-side search on filter change (debounced).
  // Compare serialized filters to detect real changes (not just reference changes),
  // and skip the initial render since data comes from SSR.
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevFiltersRef = useRef<string>(JSON.stringify(serializeFilters(filters)));
  useEffect(() => {
    const serialized = JSON.stringify(serializeFilters(filters));
    if (serialized === prevFiltersRef.current) return;
    prevFiltersRef.current = serialized;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      startTransition(async () => {
        const result = await searchCandidates(serializeFilters(filters), 0, PAGE_SIZE);
        setLoadedItems(result.items);
        setPlacementMap(result.placementMap);
        setTotalCount(result.total);
        setHasMore(result.hasMore);
      });
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [filters]);

  async function loadMore() {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const result = await searchCandidates(
      serializeFilters(filters),
      loadedItems.length,
      PAGE_SIZE
    );
    setLoadedItems((prev) => [...prev, ...result.items]);
    setPlacementMap((prev) => ({ ...prev, ...result.placementMap }));
    setHasMore(result.hasMore);
    setTotalCount(result.total);
    setLoadingMore(false);
  }

  // Track filter changes (debounced)
  const filterTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    const hasAny =
      filters.season !== null ||
      filters.roles.size > 0 ||
      filters.locations.size > 0 ||
      filters.qualifications.size > 0 ||
      filters.languages.length > 0 ||
      filters.experience !== "" ||
      filters.ageRanges.size > 0 ||
      filters.rightToWork !== "" ||
      filters.nationality !== "" ||
      filters.hasVideo;
    if (!hasAny) return;
    clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => {
      const filterTypes = [
        filters.season !== null ? "season" : null,
        filters.roles.size > 0 ? "roles" : null,
        filters.locations.size > 0 ? "locations" : null,
        filters.qualifications.size > 0 ? "qualifications" : null,
        filters.languages.length > 0 ? "languages" : null,
        filters.experience !== "" ? "experience" : null,
        filters.ageRanges.size > 0 ? "age" : null,
        filters.rightToWork !== "" ? "right_to_work" : null,
        filters.nationality !== "" ? "nationality" : null,
        filters.hasVideo ? "video" : null,
      ].filter((value): value is string => value !== null);
      trackEvent("filter_applied", {
        filter_count: filterTypes.length,
        filter_types: filterTypes,
      });
    }, 800);
    return () => clearTimeout(filterTimer.current);
  }, [filters]);

  // Debounce name search by 300ms
  const nameSearchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    nameSearchTimer.current = setTimeout(() => {
      setDebouncedNameSearch(nameSearch);
    }, 300);
    return () => clearTimeout(nameSearchTimer.current);
  }, [nameSearch]);

  // ═══ DISPLAY ═══

  const displayedCandidates = useMemo(() => {
    let items = loadedItems;
    if (showShortlistedOnly) {
      items = items.filter((item) => shortlistedSet.has(item.candidate.id));
    }
    if (debouncedNameSearch) {
      const q = debouncedNameSearch.toLowerCase();
      items = items.filter((item) => {
        const full = `${item.candidate.first_name} ${item.candidate.last_name}`.toLowerCase();
        return full.includes(q);
      });
    }
    return items;
  }, [loadedItems, showShortlistedOnly, shortlistedSet, debouncedNameSearch]);

  const hasAnyFilter =
    debouncedNameSearch !== "" ||
    filters.season !== null ||
    filters.seasonDisplayGroup !== "" ||
    filters.roleCategory !== "" ||
    filters.roles.size > 0 ||
    filters.locations.size > 0 ||
    filters.qualifications.size > 0 ||
    filters.languages.length > 0 ||
    filters.experience !== "" ||
    filters.ageRanges.size > 0 ||
    filters.gender !== "" ||
    filters.rightToWork !== "" ||
    filters.nationality !== "" ||
    filters.hasVideo;

  // ═══ HANDLERS ═══

  async function handleMessageClick(
    candidate: BrowseCandidate,
    matchingWindow: AvailabilityWindow
  ) {
    if (!userRole) {
      router.push("/employer-signup?redirect=/browse");
      return;
    }
    if (userRole !== "employer") return;
    if (!employer) return;

    // Check for existing conversation with this window
    setCheckingConversationFor(candidate.id);
    const timeout = setTimeout(() => setCheckingConversationFor(null), 8000);
    const { data: existing } = await supabase
      .from("conversations")
      .select("id")
      .eq("company_id", employer.id)
      .eq("candidate_id", candidate.id)
      .eq("availability_window_id", matchingWindow.id)
      .is("listing_id", null)
      .maybeSingle();
    clearTimeout(timeout);
    setCheckingConversationFor(null);

    if (existing) {
      router.push(`/employer/messages/${existing.id}`);
      return;
    }

    setMessageTarget({ candidate, window: matchingWindow });
  }

  function handleMessageSent(conversationId: string) {
    const name = messageTarget?.candidate.first_name ?? "the candidate";
    setMessageTarget(null);
    setToast(`Message sent to ${name}!`);
    setTimeout(() => setToast(null), 4000);
    router.push(`/employer/messages/${conversationId}`);
  }

  function handleToggleShortlist(
    candidateId: string,
    windowId: string
  ) {
    if (!isEmployer) {
      if (!userRole) router.push("/employer-signup?redirect=/browse");
      return;
    }
    const wasSaved = shortlistedSet.has(candidateId);
    setShortlistedSet((prev) => {
      const next = new Set(prev);
      if (wasSaved) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });
    toggleShortlist(candidateId, wasSaved ? undefined : windowId).then(
      (result) => {
        if (result.error) {
          setShortlistedSet((prev) => {
            const next = new Set(prev);
            if (wasSaved) next.add(candidateId);
            else next.delete(candidateId);
            return next;
          });
          setShortlistError(candidateId);
          setTimeout(() => setShortlistError(null), 3000);
        }
      }
    );
    trackEvent("shortlist_toggled", {
      candidateId,
      action: wasSaved ? "removed" : "saved",
    });
  }

  // Find conversation for a specific candidate+window
  function getConversationForWindow(
    candidateId: string,
    windowId: string
  ): ConversationEntry | undefined {
    const convs = conversationMap?.[candidateId];
    if (!convs) return undefined;
    return convs.find((c) => c.windowId === windowId) ?? convs[0];
  }

  // ═══ EMPTY STATES ═══

  function getEmptyMessage(): { title: string; description: string } {
    if (showShortlistedOnly) {
      return {
        title: "No shortlisted candidates",
        description: "You haven't shortlisted any candidates matching these filters yet.",
      };
    }
    if (filters.employerDatesActive && filters.season) {
      return {
        title: "No candidates match your specific dates",
        description: `Try removing the date filter to see everyone available for ${filters.season.label}.`,
      };
    }
    if (filters.season) {
      return {
        title: `No candidates for ${filters.season.label}`,
        description: "Try broadening your search or removing some filters.",
      };
    }
    return {
      title: "No candidates found",
      description: "Candidates are joining every day. Check back soon or browse without filters.",
    };
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-[300px_minmax(0,1fr)] min-h-screen -mt-16 pt-16">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block bg-white border-r border-border-light p-7 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto">
          <FilterSidebar
            filters={filters}
            setFilters={setFilters}
            filteredCount={showShortlistedOnly ? displayedCandidates.length : totalCount}
            nameSearch={nameSearch}
            onNameSearchChange={setNameSearch}
            showEmployerOnlyFilters={isEmployer}
          />
        </aside>

        {/* Mobile Filter Toggle */}
        <div className="md:hidden sticky top-16 z-50 bg-warm-bg/95 backdrop-blur-sm border-b border-border-light px-5 py-3">
          <div className="flex justify-between items-center mb-2.5">
            <span className="text-sm text-text-muted">
              <strong className="text-charcoal">{totalCount}</strong>{" "}
              candidate{totalCount !== 1 ? "s" : ""}
              {hasAnyFilter ? " matching" : ""}
            </span>
          </div>
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="w-full py-2.5 rounded-lg text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light flex items-center justify-center gap-2 shadow-[0_2px_8px_rgba(28, 138, 153,0.25)]"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <line x1="1" y1="3" x2="13" y2="3" />
              <line x1="3" y1="7" x2="11" y2="7" />
              <line x1="5" y1="11" x2="9" y2="11" />
            </svg>
            {(() => {
              const count =
                (filters.season ? 1 : 0) +
                (filters.seasonDisplayGroup ? 1 : 0) +
                (filters.roleCategory ? 1 : 0) +
                filters.roles.size +
                filters.locations.size +
                filters.qualifications.size +
                filters.languages.length +
                (filters.experience ? 1 : 0) +
                filters.ageRanges.size +
                (filters.gender ? 1 : 0) +
                (filters.rightToWork ? 1 : 0) +
                (filters.nationality ? 1 : 0) +
                (filters.hasVideo ? 1 : 0);
              return count > 0 ? `Filters (${count})` : "Filters";
            })()}
          </button>
          {/* Quick season group chips */}
          <div className="flex gap-2 mt-2.5">
            {(["snow", "sun", "festival"] as const).map((group) => {
              const isActive = filters.seasonDisplayGroup === group;
              return (
                <button
                  key={group}
                  onClick={() =>
                    setFilters((f) => {
                      const newGroup = isActive ? "" : group;
                      const newLocsTree = newGroup
                        ? (getLocationsForSeasonGroup(newGroup) ?? LOCATIONS_TREE)
                        : LOCATIONS_TREE;
                      const allowedLocs = new Set(getAllLeafIdsFromTree(newLocsTree));
                      return {
                        ...f,
                        seasonDisplayGroup: newGroup,
                        season: null,
                        employerDatesActive: false,
                        employerFrom: "",
                        employerTo: "",
                        locations: new Set([...f.locations].filter((id) => allowedLocs.has(id))),
                      };
                    })
                  }
                  className={`flex-1 py-1.5 rounded-full text-[12px] font-medium cursor-pointer font-sans transition-all ${
                    isActive
                      ? "bg-gold text-white border border-gold"
                      : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold active:bg-gold/5"
                  }`}
                >
                  {DISPLAY_GROUP_LABELS[group]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile Filter Panel */}
        {mobileFiltersOpen && (
          <div className="fixed inset-0 z-[100] md:hidden">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setMobileFiltersOpen(false)}
            />
            <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[360px] bg-white p-5 overflow-y-auto animate-fade-in">
              <FilterSidebar
                filters={filters}
                setFilters={setFilters}
                filteredCount={showShortlistedOnly ? displayedCandidates.length : totalCount}
                nameSearch={nameSearch}
                onNameSearchChange={setNameSearch}
                onClose={() => setMobileFiltersOpen(false)}
                showEmployerOnlyFilters={isEmployer}
              />
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="p-5 md:px-9 md:py-7">
          <h1 className="mb-3 font-heading text-[22px] font-bold leading-tight text-navy md:mb-4 md:text-[26px]">
            Browse seasonal candidates
          </h1>

          {/* Results Bar */}
          <div className="hidden md:flex justify-between items-center gap-2 mb-4">
            <div>
              {hasAnyFilter
                ? <span className="text-[15px] font-semibold text-charcoal">{totalCount} candidate{totalCount !== 1 ? "s" : ""} matching</span>
                : <span className="text-[13px] text-text-muted">Filter by season, role, location, qualifications, and more</span>}
            </div>
            <div className="flex items-center gap-2">
              {isEmployer && shortlistedSet.size > 0 && (
                <button
                  onClick={() => setShowShortlistedOnly((v) => !v)}
                  className={`px-3.5 py-2 rounded-sm text-[13px] font-semibold border-[1.5px] cursor-pointer font-sans transition-all inline-flex items-center gap-1.5 whitespace-nowrap ${
                    showShortlistedOnly
                      ? "bg-gold/10 text-gold border-gold/40 hover:bg-gold/15"
                      : "bg-white text-text-muted border-border hover:border-gold hover:text-gold"
                  }`}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill={showShortlistedOnly ? "currentColor" : "none"}
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  Saved
                </button>
              )}
            </div>
          </div>

          {/* Desktop quick season group chips */}
          <div className="hidden md:flex gap-2 mb-4">
            {(["snow", "sun", "festival"] as const).map((group) => {
              const isActive = filters.seasonDisplayGroup === group;
              return (
                <button
                  key={group}
                  onClick={() =>
                    setFilters((f) => {
                      const newGroup = isActive ? "" : group;
                      const newLocsTree = newGroup
                        ? (getLocationsForSeasonGroup(newGroup) ?? LOCATIONS_TREE)
                        : LOCATIONS_TREE;
                      const allowedLocs = new Set(getAllLeafIdsFromTree(newLocsTree));
                      return {
                        ...f,
                        seasonDisplayGroup: newGroup,
                        season: null,
                        employerDatesActive: false,
                        employerFrom: "",
                        employerTo: "",
                        locations: new Set([...f.locations].filter((id) => allowedLocs.has(id))),
                      };
                    })
                  }
                  className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium cursor-pointer font-sans transition-all ${
                    isActive
                      ? "bg-gold text-white border border-gold"
                      : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold"
                  }`}
                >
                  {DISPLAY_GROUP_LABELS[group]}
                </button>
              );
            })}
          </div>

          {/* Filter Chips */}
          {(filters.season || filters.seasonDisplayGroup || filters.roleCategory || filters.roles.size > 0) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {filters.seasonDisplayGroup && (
                <button
                  onClick={() =>
                    setFilters((f) => ({ ...f, seasonDisplayGroup: "" }))
                  }
                  className="inline-flex items-center gap-1.5 px-[11px] py-1 rounded-[20px] text-[12.5px] font-medium bg-cream text-navy border border-[rgba(0,0,0,0.10)] cursor-pointer transition-colors hover:bg-border font-sans"
                >
                  {DISPLAY_GROUP_LABELS[filters.seasonDisplayGroup] ?? filters.seasonDisplayGroup}
                  <span className="text-text-muted text-[14px] leading-none">&times;</span>
                </button>
              )}
              {filters.season && (
                <button
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      season: null,
                      employerDatesActive: false,
                      employerFrom: "",
                      employerTo: "",
                    }))
                  }
                  className="inline-flex items-center gap-1.5 px-[11px] py-1 rounded-[20px] text-[12.5px] font-medium bg-cream text-navy border border-[rgba(0,0,0,0.10)] cursor-pointer transition-colors hover:bg-border font-sans"
                >
                  {filters.season.label}
                  <span className="text-text-muted text-[14px] leading-none">&times;</span>
                </button>
              )}
              {filters.roleCategory && (
                <button
                  onClick={() => setFilters((f) => ({ ...f, roleCategory: "" }))}
                  className="inline-flex items-center gap-1.5 px-[11px] py-1 rounded-[20px] text-[12.5px] font-medium bg-cream text-navy border border-[rgba(0,0,0,0.10)] cursor-pointer transition-colors hover:bg-border font-sans"
                >
                  {ROLE_CATEGORY_LABELS[filters.roleCategory] ?? filters.roleCategory}
                  <span className="text-text-muted text-[14px] leading-none">&times;</span>
                </button>
              )}
              {!filters.roleCategory && (() => {
                const chips: { key: string; label: string; leafIds: string[] }[] = [];
                function collectChips(nodes: TreeNode[], suffix?: string) {
                  for (const node of nodes) {
                    const state = getNodeCheckState(node, filters.roles);
                    if (state === "unchecked") continue;
                    if (state === "checked" && node.children && node.children.length > 0) {
                      const displayLabel = suffix ? `${node.label} ${suffix}` : (node.id === "winter" || node.id === "summer" ? `${node.label} Roles` : node.label);
                      chips.push({ key: node.id, label: displayLabel, leafIds: getAllLeafIds(node) });
                    } else if (node.children && node.children.length > 0) {
                      collectChips(node.children);
                    } else if (filters.roles.has(node.id)) {
                      chips.push({ key: node.id, label: node.label, leafIds: [node.id] });
                    }
                  }
                }
                collectChips(ROLES_TREE);
                return chips.map((chip) => (
                  <button
                    key={chip.key}
                    onClick={() =>
                      setFilters((f) => {
                        const next = new Set(f.roles);
                        chip.leafIds.forEach((id) => next.delete(id));
                        return { ...f, roles: next };
                      })
                    }
                    className="inline-flex items-center gap-1.5 px-[11px] py-1 rounded-[20px] text-[12.5px] font-medium bg-cream text-navy border border-[rgba(0,0,0,0.10)] cursor-pointer transition-colors hover:bg-border font-sans"
                  >
                    {chip.label}
                    <span className="text-text-muted text-[14px] leading-none">&times;</span>
                  </button>
                ));
              })()}
            </div>
          )}

          {/* Candidate Cards */}
          <div className={`flex flex-col gap-4 transition-opacity duration-200 ${isPending ? "opacity-60 pointer-events-none" : ""}`}>
            {displayedCandidates.length === 0 ? (
              <div className="text-center py-20">
                <svg className="w-12 h-12 mx-auto mb-4 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <h3 className="font-serif text-2xl mb-2">
                  {getEmptyMessage().title}
                </h3>
                <p className="text-sm text-text-muted max-w-md mx-auto mb-4">
                  {getEmptyMessage().description}
                </p>
                {!showShortlistedOnly && (
                  <p className="text-[13px] text-text-muted max-w-md mx-auto mb-6">
                    Try broadening your date range or adjusting your filters.
                  </p>
                )}
                <button
                  onClick={() => {
                    if (showShortlistedOnly) setShowShortlistedOnly(false);
                    else if (filters.employerDatesActive) {
                      setFilters((f) => ({
                        ...f,
                        employerDatesActive: false,
                        employerFrom: f.season?.from ?? "",
                        employerTo: f.season?.to ?? "",
                      }));
                    } else {
                      setFilters(INITIAL_FILTERS);
                    }
                  }}
                  className="px-6 py-3 rounded-sm text-[13px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light"
                >
                  {showShortlistedOnly
                    ? "Show all candidates"
                    : filters.employerDatesActive
                      ? "Remove date filter"
                      : "Clear all filters"}
                </button>
              </div>
            ) : (
              displayedCandidates.map(({ candidate, match }, i) => (
                <CandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  matchResult={match}
                  index={i}
                  onMessage={() =>
                    handleMessageClick(candidate, match.primaryWindow)
                  }
                  conversationInfo={getConversationForWindow(
                    candidate.id,
                    match.primaryWindow.id
                  )}
                  isShortlisted={shortlistedSet.has(candidate.id)}
                  onToggleShortlist={() =>
                    handleToggleShortlist(candidate.id, match.primaryWindow.id)
                  }
                  onPlayVideo={setVideoCandidate}
                  isCheckingConversation={checkingConversationFor === candidate.id}
                  shortlistError={shortlistError === candidate.id}
                  placementMap={placementMap}
                  userRole={userRole}
                  linkedCount={linkedCounts[candidate.id] ?? 0}
                  vouchCount={vouchCounts[candidate.id] ?? 0}
                />
              ))
            )}
          </div>

          {(hasMore || loadedItems.length < totalCount) && !showShortlistedOnly && (
            <div className="flex justify-center mt-8 mb-4">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-8 py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-60"
              >
                {loadingMore
                  ? "Loading..."
                  : `Load more (${totalCount - loadedItems.length} remaining)`}
              </button>
            </div>
          )}

          {isPending && loadedItems.length > 0 && (
            <div className="flex items-center justify-center gap-2 py-4 text-text-muted text-sm">
              <div className="w-4 h-4 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
              <span>Updating results…</span>
            </div>
          )}

        </main>
      </div>

      {/* Message Modal */}
      {messageTarget && employer && (
        <MessageModal
          candidate={messageTarget.candidate}
          matchingWindow={messageTarget.window}
          employer={employer}
          onClose={() => setMessageTarget(null)}
          onSent={handleMessageSent}
        />
      )}

      {/* Video Modal */}
      {videoCandidate && videoCandidate.video_url && (
        <VideoModal
          candidate={videoCandidate}
          onClose={() => setVideoCandidate(null)}
        />
      )}

      {/* Rate limit error modal */}
      {rateLimitError && (
        <div
          className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center backdrop-blur-[4px] p-4"
          onClick={() => setRateLimitError(null)}
        >
          <div className="bg-white rounded-[20px] w-full max-w-[420px] p-8 text-center shadow-[0_24px_80px_rgba(0,0,0,0.2)] animate-fade-in">
            <div className="text-4xl mb-3">{"\u23F0"}</div>
            <h3 className="font-serif text-xl mb-2">Daily limit reached</h3>
            <p className="text-[13px] text-text-muted mb-2">
              {rateLimitError}
            </p>
            <p className="text-[12px] text-text-muted mb-6">
              Your limit resets at midnight.
            </p>
            <button
              onClick={() => setRateLimitError(null)}
              className="px-6 py-3 rounded-sm text-[13px] font-semibold bg-navy text-white border-none cursor-pointer font-sans"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Welcome toast */}
      {showWelcome && (
        <div className="fixed top-20 right-5 z-[300] bg-white rounded-md shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-success/20 px-5 py-4 animate-fade-in max-w-[340px]">
          <p className="text-sm font-semibold text-success mb-0.5">
            Account created!
          </p>
          <p className="text-xs text-text-muted">
            Start browsing candidates and send messages when you find the right
            fit.
          </p>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className="fixed top-20 right-5 z-[300] bg-white rounded-md shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-success/20 px-5 py-4 animate-fade-in max-w-[340px]">
          <p className="text-sm font-semibold text-success">{toast}</p>
        </div>
      )}
    </>
  );
}
